<template>
  <span ref="categoriesLink" class="nav-item__tier-0" :class="{'sr-only': !navDataLoaded}" role="none">
    <a
      v-if="hasOverlay && occSuccessful"
      :id="linkId"
      :key="linkId"
      :qaid="`nav-categories-link-${linkId}`"
      :href="composeLink(href)"
      :target="setTarget(target)"
      role="menuitem"
      :class="classes"
      class="nav__menu-item nav__mobile-item cursor-pointer font-bold px-2 py-2 flex justify-between lg:px-1"
      aria-expanded="false"
      v-bind="getGaAttributes()"
      @click.prevent="focusNewTier"
      @keydown.prevent.enter="focusNewTier"
      @keydown.prevent.right="
        (e) => {
          !isCompactView ? focusNextMenuItem(e) : focusNewTier(e);
        }
      "
      @keydown.prevent.left="
        (e) => {
          !isCompactView ? focusPreviousMenuItem(e) : toggleMobileNav();
        }
      "
      @keydown.prevent.down="
        (e) => {
          !isCompactView ? focusNewTier(e) : focusNextMobileMenuItem(e);
        }
      "
      @keydown.prevent.up="
        (e) => {
          !isCompactView ? '' : focusPreviousMobileMenuItem(e);
        }
      "
    >
      <span class="flex items-center gap-1 pointer-events-none relative whitespace-nowrap">
        {{ linkText }}
      </span>
      <pdl-icon class="lg:hidden pointer-events-none" name="chevron_right" />
    </a>
    <tier-one-nav-items
      v-if="hasOverlay && occSuccessful"
      :is-b2b="isB2b"
      :label="linkText"
      :nav-item-data="navCategoriesLinkData"
    />
    <a
      v-else-if="isLinkVisible"
      ref="navItem"
      role="menuitem"
      :class="classes"
      class="nav__menu-item nav__mobile-item cursor-pointer flex lone font-bold px-2 py-2 lg:px-1"
      :href="composeLink(href)"
      :target="setTarget(target)"
      :qaid="`nav-categories-link-${linkId}`"
      @keydown.prevent.right="
        (e) => {
          !isCompactView ? focusNextMenuItem(e) : '';
        }
      "
      @keydown.prevent.left="
        (e) => {
          !isCompactView ? focusPreviousMenuItem(e) : toggleMobileNav();
        }
      "
      @keydown.prevent.down="
        (e) => {
          !isCompactView ? '' : focusNextMobileMenuItem(e);
        }
      "
      @keydown.prevent.up="
        (e) => {
          !isCompactView ? '' : focusPreviousMobileMenuItem(e);
        }
      "
    >
      <span class="flex items-center gap-1 pointer-events-none relative whitespace-nowrap">
        {{ linkText }}
      </span>
    </a>
  </span>
</template>

<script>
import {mapState} from 'vuex';
import links from '@/navigation/mixins/links.js';
import TierOneNavItems from '@/navigation/TierOneNavItems.vue';
import NavApi from '@/navigation/utils/nav-api.js';
import store from '@/store';
import {PdlIcon} from '@pedal/pdl-icon';

const hiddenLinksInKioskMode = ['Tier1Link_ElectraShops', 'Tier1Link_b2cLocator'];
export default {
  name: 'NavCategoriesLink',
  components: {TierOneNavItems, PdlIcon},
  mixins: [links],
  inject: [
    'navDataLoaded',
    'focusNewTier',
    'isB2b',
    'isElectra',
    'focusNextMenuItem',
    'focusPreviousMenuItem',
    'focusNextMobileMenuItem',
    'focusPreviousMobileMenuItem',
    'toggleMobileNav',
    'handleNavOccCallCompleted',
  ],
  props: {
    uid: {
      type: String,
      default: null,
    },
    /**
     * The uid of the link. This is used to set the "activeLink" in VueX.
     */
    id: {
      type: String,
      default: null,
    },
    /**
     * The text of the link
     */
    linkText: {
      type: String,
      default: null,
    },
    /**
     * The href for the link with no overlay
     */
    href: {
      type: String,
      default: null,
    },

    target: {
      type: String,
      default: null,
    },
    isAutoExpandMobile: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, links without products in this category's overlay will be hidden
     */
    hideLinksWithoutProducts: {
      type: Boolean,
      default: false,
    },
    /**
     * How many links should be in each column for auto-wrapping singular lists
     */
    minLinksPerCol: {
      type: Number,
      default: 5,
    },
    gaAttributes: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      navCategoriesLinkData: [],
      occSuccessful: false,
    };
  },
  computed: {
    ...mapState('global', ['isCompactView']),
    ...mapState('backend', ['kioskModeEnabledOnSite', 'currentB2BUnit', 'currentB2bUnitUserRoles']),

    hasOverlay() {
      return this.navCategoriesLinkData.length > 0;
    },
    linkId() {
      return this.id;
    },
    classes() {
      return {
        'lg:text-xs xl:text-sm xxl:px-[.75rem]': !this.isB2b,
        'lg:text-xs xxl:text-sm': this.isB2b,
      };
    },
    isLinkVisible() {
      if (!this.kioskModeEnabledOnSite) {
        return true;
      }
      return !hiddenLinksInKioskMode.includes(this.id);
    },
  },
  async mounted() {
    await this.getNavItems();
    this.setParentRoleAttribute();
  },
  methods: {
    async getNavItems() {
      try {
        const occParams = store.getters['backend/occUrlParams'];
        const params = {
          userId: occParams.userId,
          componentId: this.uid,
          b2bUnitId: this.isB2b ? this.currentB2BUnit : null,
          b2bUserRoles: this.currentB2bUnitUserRoles?.join(',') || '',
        };
        const response = await NavApi.getNavDataByNodeID(params);
        if (response) {
          this.occSuccessful = true;
          return (this.navCategoriesLinkData = response);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.$nextTick(() => {
          this.handleNavOccCallCompleted();
        });
      }
    },
    getGaAttributes() {
      return {
        'data-ga-event': this.gaAttributes?.dataGaEvent,
        'data-ga-category': this.gaAttributes?.dataGaCategory,
        'data-ga-action': this.gaAttributes?.dataGaAction,
        'data-ga-label': this.gaAttributes?.dataGaLabel,
        'data-asset-id': this.gaAttributes?.dataAssetId,
      };
    },
    setParentRoleAttribute() {
      this.$refs.categoriesLink.parentNode.setAttribute('role', 'none');
    },
  },
};
</script>
<style lang="scss">
.nav-item__tier-0 > a {
  @media (screen(lg)) {
    height: 54px;
    max-height: 54px;

    > span {
      line-height: 22px; // 54px height - 16px padding top/bottom = 22px
    }
  }
}
</style>
